/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Grid, Snackbar, Typography } from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { useGetSignedUserOrUserFromState } from '../../hooks/auth.ts';
import { useGetCustomer } from '../../api/contact';
import { useGetImages, useUpdateImageRank } from '../../api/styleBoard';
import { StyleBoardImage } from './StyleBoardImage';
import { UploadButton } from './UploadButton';
import { UploadResponseItem } from '../../models/cloudinary.models';
import { DescriptionModal } from './DescriptionModal';
import { DeleteImageModal } from './DeleteImageModal';
import './StyleBoard.scss';
import { useTitle } from '../../hooks/useTitle.ts';

export const StyleBoard = () => {
  useTitle('Style Board - Immersive Homes');
  const user = useGetSignedUserOrUserFromState();

  const { data: customerData, loading: customerLoading } = useGetCustomer();
  const { data } = useGetImages(`${user?.userId}` || '');
  const [selectedImage, setSelectedImage] = useState<
    UploadResponseItem | undefined
  >();
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [updateImages, setUpdateImages] = useState(false);

  const openModal = (image: UploadResponseItem) => {
    setSelectedImage(image);
    setOpen(true);
  };

  const openDeleteModal = (image: UploadResponseItem) => {
    setSelectedImage(image);
    setDeleteOpen(true);
  };

  const ResetSelectedImage = () => {
    setSelectedImage(undefined);
  };

  const mutation = useUpdateImageRank();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Takes response data from react query response and attaches an id to each image based on priority_rank metadata.
  // This is necessary for the drag and drop functionality to work.
  const getItems = useCallback(() => {
    return (
      data
        .map((item) => {
          return {
            id: getRank(item),
            image: item,
          };
        })
        .sort((a, b) => b.id - a.id) || []
    );
  }, [data]);

  const getRank = (item: UploadResponseItem) => {
    return Number(
      item.metadata.find((item) => item.label === 'priority_rank')?.value
    );
  };

  const [items, setItems] = useState(getItems());

  const updateItems = () => {
    items.forEach((item, index) => {
      const rank = Math.abs(items.length - index);
      const imageFromCache = data.find((image) => {
        return image?.public_id === item.image.public_id;
      });
      if (imageFromCache && getRank(imageFromCache) !== rank) {
        mutation.mutate({ image: item.image, rank: rank });
      }
    });
  };

  useEffect(() => {
    if (items.length > 0) {
      updateItems();
    }
  }, [items]);

  useEffect(() => {
    // If we've uploaded/deleted an image refresh items list from react query.
    if (data && items && data.length !== items.length) {
      setItems(getItems());
    }
    if (updateImages) {
      setItems(getItems());
      setUpdateImages(false);
    }
  }, [data, updateImages]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id && over) {
      setItems((items) => {
        const oldIndex = items.findIndex(
          (item) => getRank(item.image) === active.id
        );
        const newIndex = items.findIndex(
          (item) => getRank(item.image) === over.id
        );
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <Box
      data-testid="style-board"
      className="style-page"
      sx={{
        padding: '64px',
        paddingTop: '24px',
        width: '100%',
        height: '100%',
      }}
    >
      <Box className="upper-container">
        {user ? (
          <Typography
            sx={{ fontSize: '38px', paddingBottom: '24px' }}
            variant="h2"
          >
            {customerLoading
              ? 'Loading...'
              : customerData?.firstName && customerData?.lastName
                ? `${customerData.firstName} ${customerData.lastName}'s Style Inspiration`
                : 'My Inspiration'}
          </Typography>
        ) : (
          <Typography
            sx={{ fontSize: '38px', paddingBottom: '24px' }}
            variant="h2"
          >
            My Inspiration
          </Typography>
        )}
        <Box className="upload-container">
          <Typography sx={{ maxWidth: '700px' }}>
            Share photos of homes, lots, finishes and more that highlight your
            tastes and desires to better shape our build together.
          </Typography>
          <UploadButton />
        </Box>
      </Box>
      {data && (
        <>
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <SortableContext items={items || []}>
              <Grid
                container
                spacing={1}
                sx={{
                  height: '90%',
                  width: '100%',
                  overflow: 'auto',
                }}
              >
                {data &&
                  items?.map((image) => (
                    <StyleBoardImage
                      index={getRank(image.image)}
                      key={getRank(image.image)}
                      image={image.image}
                      openModal={openModal}
                      openDeleteModal={openDeleteModal}
                    />
                  ))}
              </Grid>
            </SortableContext>
          </DndContext>
          {selectedImage && (
            <>
              <DescriptionModal
                image={selectedImage}
                open={open}
                setOpen={setOpen}
                ResetSelectedImage={ResetSelectedImage}
                setShowSuccess={setShowSuccess}
                setShowError={setShowError}
                setUpdateImages={setUpdateImages}
              />
              <DeleteImageModal
                image={selectedImage}
                open={deleteOpen}
                setOpen={setDeleteOpen}
                ResetSelectedImage={ResetSelectedImage}
                setShowError={setShowError}
              />
            </>
          )}
          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            open={showError}
            autoHideDuration={3000}
            onClose={() => setShowError(false)}
          >
            <Alert severity="error">
              Something went wrong. Please try again
            </Alert>
          </Snackbar>
          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            open={showSuccess}
            autoHideDuration={3000}
            onClose={() => setShowSuccess(false)}
          >
            <Alert severity="success">Image Updated</Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
};
